<template>
  <div>
    <button
      v-if="showFloatEffective"
      type="button"
      class="dialogue-float"
      :class="{ 'has-unread': hasUnread }"
      title="打开对话中心"
      :style="dialogueFloatStyle"
      @pointerdown="onDialogueFloatPointerDown"
      @click="onDialogueFloatClick"
      @dragstart.prevent
    >
      <span v-if="hasUnread" class="dialogue-float-badge">{{ unreadBadgeText }}</span>
    </button>

    <SchemeEcosystemDialogueCenter
      :key="dialogueInstanceKey"
      :embedded="embedded"
      :visible.sync="dialogueVisible"
      :launch-query="resolvedLaunchQuery"
      v-bind="passthroughAttrs"
      v-on="$listeners"
      @unread-change="handleUnreadChange"
      @close="handleInnerClose"
    />
  </div>
</template>

<script>
import SchemeEcosystemDialogueCenter from '@/views/schemeEcosystem/pages/DialogueCenter.vue'

export default {
  name: 'DialogueCenter',
  components: {
    SchemeEcosystemDialogueCenter
  },
  inheritAttrs: false,
  props: {
    embedded: {
      type: Boolean,
      default: true
    },
    visible: {
      type: Boolean,
      default: false
    },
    launchQuery: {
      type: Object,
      default: null
    },
    showFloat: {
      type: Boolean,
      default: true
    },
    resetFloatOnMount: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      dialogueVisible: !!this.visible,
      dialogueInstanceKey: 0,
      localLaunchQuery: this.launchQuery,
      dialogueFloatPos: null,
      dialogueFloatDragging: false,
      dialogueFloatDragStart: null,
      dialogueFloatDragMoved: false,
      floatOwnerKey: `dc-float-owner-${Date.now()}-${Math.random().toString(16).slice(2)}`,
      isFloatOwner: false,
      unreadCount: 0
    }
  },
  computed: {
    showFloatEffective() {
      return !!this.showFloat && !!this.isFloatOwner
    },
    passthroughAttrs() {
      const attrs = { ...(this.$attrs || {}) }
      delete attrs.embedded
      delete attrs.visible
      delete attrs.launchQuery
      delete attrs['launch-query']
      delete attrs.showFloat
      delete attrs['show-float']
      return attrs
    },
    resolvedLaunchQuery() {
      return this.launchQuery != null ? this.launchQuery : this.localLaunchQuery
    },
    hasUnread() {
      return Number(this.unreadCount || 0) > 0
    },
    unreadBadgeText() {
      const n = Number(this.unreadCount || 0)
      if (n <= 0) return ''
      return n > 99 ? '99+' : String(n)
    },
    dialogueFloatStyle() {
      const size = 64
      const margin = 18
      const w = typeof window !== 'undefined' ? Number(window.innerWidth || 0) : 0
      const h = typeof window !== 'undefined' ? Number(window.innerHeight || 0) : 0
      const fallbackLeft = Math.max(margin, w ? w - size - margin : margin)
      const fallbackTop = Math.max(margin, h ? h - size - margin : margin)
      const left = this.dialogueFloatPos && this.dialogueFloatPos.x != null ? Number(this.dialogueFloatPos.x) : fallbackLeft
      const top = this.dialogueFloatPos && this.dialogueFloatPos.y != null ? Number(this.dialogueFloatPos.y) : fallbackTop
      return {
        left: `${left}px`,
        top: `${top}px`
      }
    }
  },
  watch: {
    visible(val) {
      this.dialogueVisible = !!val
    },
    launchQuery(val) {
      this.localLaunchQuery = val
      if (val && typeof val === 'object') {
        this.dialogueInstanceKey += 1
      }
    },
    dialogueVisible(val) {
      this.$emit('update:visible', !!val)
    }
  },
  mounted() {
    if (typeof window !== 'undefined') {
      this.acquireFloatOwner()
      if (this.resetFloatOnMount) {
        try {
          window.localStorage.removeItem('schemeEco.dialogueFloatPos')
        } catch (e) {
          // ignore
        }
        this.dialogueFloatPos = null
      }
      if (this.isFloatOwner) {
        window.addEventListener('scheme-eco-open-dialogue', this.handleGlobalDialogueOpen)
        this.loadFloatPos()
      }
    }
  },
  beforeDestroy() {
    if (typeof window !== 'undefined') {
      if (this.isFloatOwner) {
        window.removeEventListener('scheme-eco-open-dialogue', this.handleGlobalDialogueOpen)
      }
      this.releaseFloatOwner()
    }
    this.teardownDialogueFloatDrag()
  },
  methods: {
    acquireFloatOwner() {
      if (typeof window === 'undefined') return
      const key = '__schemeEcoDialogueFloatOwner__'
      const owner = window[key]
      if (!owner) {
        window[key] = this.floatOwnerKey
        this.isFloatOwner = true
      } else {
        this.isFloatOwner = owner === this.floatOwnerKey
      }
    },
    releaseFloatOwner() {
      if (typeof window === 'undefined') return
      const key = '__schemeEcoDialogueFloatOwner__'
      if (window[key] === this.floatOwnerKey) {
        delete window[key]
      }
      this.isFloatOwner = false
    },
    loadFloatPos() {
      try {
        const raw = window.localStorage.getItem('schemeEco.dialogueFloatPos')
        if (!raw) return
        const parsed = JSON.parse(raw)
        if (parsed && parsed.x != null && parsed.y != null) {
          this.dialogueFloatPos = this.clampDialogueFloatPos(parsed)
        }
      } catch (e) {
        // ignore
      }
    },
    saveFloatPos() {
      try {
        if (!this.dialogueFloatPos) return
        window.localStorage.setItem('schemeEco.dialogueFloatPos', JSON.stringify(this.dialogueFloatPos))
      } catch (e) {
        // ignore
      }
    },
    clampDialogueFloatPos(pos) {
      const size = 64
      const margin = 10
      const w = typeof window !== 'undefined' ? Number(window.innerWidth || 0) : 0
      const h = typeof window !== 'undefined' ? Number(window.innerHeight || 0) : 0
      if (!w || !h) return pos
      const x = Math.min(Math.max(margin, Number(pos.x || 0)), Math.max(margin, w - size - margin))
      const y = Math.min(Math.max(margin, Number(pos.y || 0)), Math.max(margin, h - size - margin))
      return { x, y }
    },
    setupDialogueFloatDrag() {
      if (typeof window === 'undefined') return
      window.addEventListener('pointermove', this.onDialogueFloatPointerMove)
      window.addEventListener('pointerup', this.onDialogueFloatPointerUp)
      window.addEventListener('pointercancel', this.onDialogueFloatPointerUp)
    },
    teardownDialogueFloatDrag() {
      if (typeof window === 'undefined') return
      window.removeEventListener('pointermove', this.onDialogueFloatPointerMove)
      window.removeEventListener('pointerup', this.onDialogueFloatPointerUp)
      window.removeEventListener('pointercancel', this.onDialogueFloatPointerUp)
    },
    onDialogueFloatPointerDown(e) {
      if (!e) return
      if (!this.showFloatEffective) return
      if (e.button != null && e.button !== 0) return
      if (e.preventDefault) e.preventDefault()
      const target = e.currentTarget
      try {
        if (target && target.setPointerCapture && e.pointerId != null) target.setPointerCapture(e.pointerId)
      } catch (err) {
        // ignore
      }
      const style = this.dialogueFloatStyle
      const left = Number(String(style.left || '0').replace('px', '')) || 0
      const top = Number(String(style.top || '0').replace('px', '')) || 0
      this.dialogueFloatDragging = true
      this.dialogueFloatDragMoved = false
      this.dialogueFloatDragStart = {
        pointerId: e.pointerId,
        x: e.clientX,
        y: e.clientY,
        left,
        top
      }
      this.setupDialogueFloatDrag()
    },
    onDialogueFloatPointerMove(e) {
      if (!this.dialogueFloatDragging || !this.dialogueFloatDragStart) return
      if (e && e.preventDefault) e.preventDefault()
      if (e.pointerId != null && this.dialogueFloatDragStart.pointerId != null && e.pointerId !== this.dialogueFloatDragStart.pointerId) return
      const dx = Number(e.clientX) - Number(this.dialogueFloatDragStart.x)
      const dy = Number(e.clientY) - Number(this.dialogueFloatDragStart.y)
      const next = this.clampDialogueFloatPos({
        x: Number(this.dialogueFloatDragStart.left) + dx,
        y: Number(this.dialogueFloatDragStart.top) + dy
      })
      this.dialogueFloatPos = next
      if (!this.dialogueFloatDragMoved) {
        const dist = Math.sqrt(dx * dx + dy * dy)
        if (dist > 6) this.dialogueFloatDragMoved = true
      }
    },
    onDialogueFloatPointerUp(e) {
      if (!this.dialogueFloatDragging) return
      if (e && e.pointerId != null && this.dialogueFloatDragStart?.pointerId != null && e.pointerId !== this.dialogueFloatDragStart.pointerId) return
      this.dialogueFloatDragging = false
      this.dialogueFloatDragStart = null
      this.teardownDialogueFloatDrag()
      this.saveFloatPos()
      if (this.dialogueFloatDragMoved) {
        setTimeout(() => {
          this.dialogueFloatDragMoved = false
        }, 0)
      }
    },
    onDialogueFloatClick() {
      if (!this.showFloatEffective) return
      if (this.dialogueFloatDragMoved) return
      this.dialogueVisible = true
    },
    handleGlobalDialogueOpen(event) {
      const payload = event && event.detail ? event.detail : null
      if (payload && typeof payload === 'object') {
        this.localLaunchQuery = { ...payload }
        this.dialogueInstanceKey += 1
      }
      this.dialogueVisible = true
    },
    handleInnerClose() {
      this.dialogueVisible = false
      this.$emit('close')
    },
    handleUnreadChange(count) {
      const next = Number(count || 0)
      this.unreadCount = Number.isFinite(next) ? Math.max(0, next) : 0
    }
  }
}
</script>

<style>
.dialogue-float {
  position: fixed;
  width: 64px;
  height: 64px;
  border: none;
  border-radius: 999px;
  background: transparent;
  cursor: pointer;
  box-shadow: 0 16px 34px rgba(37, 99, 235, 0.22);
  z-index: 60;
  padding: 0;
  touch-action: none;
  user-select: none;
  -webkit-user-select: none;
  -webkit-user-drag: none;
}

.dialogue-float::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 999px;
  background: #0b84ff;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.26);
}

.dialogue-float.has-unread::before {
  background: #f59e0b;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.32);
}

.dialogue-float::after {
  content: '';
  position: absolute;
  left: 10px;
  top: 14px;
  width: 44px;
  height: 36px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='88' height='72' viewBox='0 0 88 72'%3E%3Cpath d='M44 4C22.7 4 5.5 18.7 5.5 36.8c0 9.7 5.2 18.4 13.6 24.3-.6 2.6-2 7.7-5.3 11.7-.5.6 0 1.5.8 1.4 7.6-1.2 13.5-4.6 17.3-7.3 4.1 1.1 8.4 1.7 12.8 1.7 21.3 0 38.5-14.7 38.5-32.8S65.3 4 44 4Z' fill='%23ffffff'/%3E%3Ccircle cx='31' cy='37' r='6.5' fill='%230b84ff'/%3E%3Ccircle cx='44' cy='37' r='6.5' fill='%230b84ff'/%3E%3Ccircle cx='57' cy='37' r='6.5' fill='%230b84ff'/%3E%3C/svg%3E");
}

.dialogue-float.has-unread::after {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='88' height='72' viewBox='0 0 88 72'%3E%3Cpath d='M44 4C22.7 4 5.5 18.7 5.5 36.8c0 9.7 5.2 18.4 13.6 24.3-.6 2.6-2 7.7-5.3 11.7-.5.6 0 1.5.8 1.4 7.6-1.2 13.5-4.6 17.3-7.3 4.1 1.1 8.4 1.7 12.8 1.7 21.3 0 38.5-14.7 38.5-32.8S65.3 4 44 4Z' fill='%23ffffff'/%3E%3Ccircle cx='31' cy='37' r='6.5' fill='%23f59e0b'/%3E%3Ccircle cx='44' cy='37' r='6.5' fill='%23f59e0b'/%3E%3Ccircle cx='57' cy='37' r='6.5' fill='%23f59e0b'/%3E%3C/svg%3E");
}

.dialogue-float-badge {
  position: absolute;
  top: -6px;
  right: -4px;
  min-width: 22px;
  height: 22px;
  border-radius: 999px;
  padding: 0 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #ef4444;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  border: 2px solid #fff;
  box-shadow: 0 8px 18px rgba(239, 68, 68, 0.32);
}

.dialogue-float:hover {
  filter: brightness(1.05);
}

@media (max-width: 960px) {
  .dialogue-float {
    width: 60px;
    height: 60px;
  }
}
</style>

